import { caughtErrorLogFields, isClientLoggedError } from "@pandas/http-client";
import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import logger from "@/lib/logging/logger";

export function delay(ms: number) {
	return new Promise((resolve) => setTimeout(resolve, ms));
}

export const displayPrice = (price: number, currency: string | undefined, locale: string) =>
	Intl.NumberFormat(locale, {
		style: "currency",
		currency: currency || "EUR",
		minimumFractionDigits: 0,
	}).format(price);

export const displayPromo = (amount: number, type: string, currency: string | undefined, locale: string) => {
	if (type === "percentage") {
		return `+ ${amount} %`;
	} else {
		return `+ ${displayPrice(amount, currency, locale)}`;
	}
};

export class AbortError extends Error {
	constructor(message: string = "Upload aborted") {
		super(message);
		this.name = "AbortError";
	}
}

export const isAbortError = (error: unknown): boolean => {
	return error instanceof AbortError;
};

const isDeterministicError = (error: unknown): boolean => {
	if (typeof DOMException !== "undefined" && error instanceof DOMException) return true;
	return (
		error instanceof TypeError ||
		error instanceof SyntaxError ||
		error instanceof RangeError ||
		error instanceof ReferenceError
	);
};

// Added fnName for clearer logging on production errors where function names are minified
export const withRetries = async <T>(retries: number, fn: () => Promise<T>, fnName: string): Promise<T> => {
	try {
		return await fn();
	} catch (error) {
		if (isAbortError(error)) throw error;
		if (isClientLoggedError(error)) {
			logger.info("FLOW", `Failed to execute ${fnName}`, caughtErrorLogFields(error));
		} else {
			logger.warning("FLOW", `Failed to execute ${fnName}`, caughtErrorLogFields(error));
		}
		if (isDeterministicError(error)) throw error;
		if (retries > 0) {
			await new Promise((res) => setTimeout(res, 300));
			return withRetries(retries - 1, fn, fnName);
		}
		throw error;
	}
};

export function mergeClasses(...inputs: ClassValue[]) {
	return twMerge(clsx(inputs));
}

export const isAndroidWebView = () => {
	const userAgent = navigator.userAgent;
	return /Android.*wv\)|.*Android.*Version\/.*Chrome/.test(userAgent);
};

export const isAppleDevice = () => {
	const userAgent = navigator.userAgent;
	return /iPad|iPhone|iPod/.test(userAgent);
};

/**
 * The promise, rejecting with `message` past the bound. For awaits that sit outside any request
 * timeout — a dependency that never settles would otherwise stall its caller silently and forever.
 */
export async function withTimeout<T>(promise: Promise<T>, ms: number, message: string): Promise<T> {
	let timer: ReturnType<typeof setTimeout> | undefined;
	const timedOut = new Promise<never>((_, reject) => {
		timer = setTimeout(() => reject(new Error(message)), ms);
	});
	try {
		return await Promise.race([promise, timedOut]);
	} finally {
		clearTimeout(timer);
	}
}
